/**
 * HomelabOverview.jsx — Stained Glass Guard-House & Homelab Status Board.
 *
 * Synchronized with pi.hole v6 Relay Shields, Soul Gem Persona Drift,
 * live ESO Relay status, and Owner-Gated Dwemer Safe Maintenance Interlock.
 */
import { For, Show, createSignal, createEffect } from 'solid-js';
import useWidgetData from './useWidgetData.js';
import wm from '../lib/wmStore.js';
import toast from '../lib/toastStore.js';
import ConfirmDialog from '../components/ConfirmDialog.jsx';

function fmtUptime(sec) {
  if (!sec) return '—';
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  return d > 0 ? `${d}d ${h}h` : `${h}h ${Math.floor((sec % 3600) / 60)}m`;
}

export default function HomelabOverview(props) {
  const { data, loading, error, refreshing, refetch } = useWidgetData('homelab-overview', () => ({}));
  const isOwner = () => !!(props.identity?.isOwner || wm.state.identity?.isOwner);

  // ── Maintenance Interlock State ──
  const [maintStatus, setMaintStatus] = createSignal(null);
  const [maintBusy,   setMaintBusy]   = createSignal(false);
  const [confirm,     setConfirm]     = createSignal(null);

  async function fetchMaintStatus() {
    if (!isOwner()) return;
    try {
      const res = await fetch('/api/v2/admin/server/status', { credentials: 'same-origin' });
      if (res.ok) {
        const d = await res.json();
        setMaintStatus(d);
      }
    } catch { /* tolerate */ }
  }

  createEffect(() => {
    if (isOwner()) {
      fetchMaintStatus();
    }
  });

  async function executePark() {
    setMaintBusy(true);
    try {
      const res = await fetch('/api/v2/admin/server/park', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
      });
      const d = await res.json();
      if (!res.ok || d.ok === false) throw new Error(d.error || 'Park failed');
      toast.success('Workloads quiesced & write caches synchronized.');
      await fetchMaintStatus();
    } catch (err) {
      toast.error('Quiesce failed', { detail: err.message });
    } finally {
      setMaintBusy(false);
    }
  }

  async function executeResume() {
    setMaintBusy(true);
    try {
      const res = await fetch('/api/v2/admin/server/resume', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
      });
      const d = await res.json();
      if (!res.ok || d.ok === false) throw new Error(d.error || 'Resume failed');
      toast.success('All server workloads and containers resumed!');
      await fetchMaintStatus();
      await refetch();
    } catch (err) {
      toast.error('Resume failed', { detail: err.message });
    } finally {
      setMaintBusy(false);
    }
  }

  async function executePoweroff() {
    setMaintBusy(true);
    try {
      await fetch('/api/v2/admin/server/poweroff', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
      });
      toast.success('Server is powering off now. Goodbye!');
    } catch (err) {
      toast.error('Poweroff trigger failed', { detail: err.message });
    } finally {
      setMaintBusy(false);
    }
  }

  function promptPark() {
    setConfirm({
      title: 'Quiesce Server for Power Cord Maintenance?',
      body: 'Phase 1 will cleanly stop heavy Docker containers (Postgres, Immich, Frigate, Qdrant) and flush all disk write buffers down to zero. The Redfur Dashboard web bridge will remain online so you can verify the status before pulling the cord.',
      confirmLabel: 'Park Workloads',
      confirmKind: 'warning',
      action: executePark,
    });
  }

  function promptPoweroff() {
    setConfirm({
      title: '🔴 Execute Full System Power Off?',
      body: 'This will issue a system halt immediately. Once halted, you may unplug the power cord or press the chassis power button to restart.',
      confirmLabel: 'Power Off Now',
      confirmKind: 'danger',
      action: executePoweroff,
    });
  }

  return (
    <div class="v2-widget v2-homelab-overview">
      <div class="v2-widget-hdr">
        <span class="v2-widget-hdr__title">🏰 Guard-House & Homelab Stained Glass</span>
        <div style={{ 'margin-left': 'auto', display: 'flex', 'align-items': 'center', gap: '8px' }}>
          <a
            href="http://pi.hole/"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              'font-size': '11px', color: 'var(--cyan, #38bdf8)',
              'text-decoration': 'none', padding: '2px 8px',
              border: '1px solid rgba(56,189,248,0.3)', 'border-radius': '4px',
              background: 'rgba(56,189,248,0.1)'
            }}
          >
            Open Guard-House ↗
          </a>
          <Show when={refreshing()}>
            <span style={{ 'font-size': '11px', opacity: 0.5 }}>refreshing…</span>
          </Show>
        </div>
      </div>

      <Show when={error()} fallback={
        <Show when={!loading()} fallback={<div class="v2-widget-skel">Loading Stained Glass Matrix…</div>}>
          <div class="v2-widget__scroll" style={{ padding: 'var(--sp-2)' }}>

            {/* Relay Shields & Pi-hole Matrix */}
            <Show when={data()?.pihole}>
              <div style={{
                display: 'grid', 'grid-template-columns': '1fr 1fr', gap: '8px',
                'margin-bottom': 'var(--sp-2)',
              }}>
                <div style={{
                  background: 'linear-gradient(135deg, rgba(16,30,46,0.9), rgba(12,20,32,0.85))',
                  border: '1px solid rgba(56,189,248,0.35)', 'border-radius': '8px',
                  padding: '10px 12px', 'box-shadow': '0 4px 14px rgba(0,0,0,0.25)'
                }}>
                  <div style={{ 'font-size': '10px', 'text-transform': 'uppercase', 'letter-spacing': '0.08em', opacity: 0.7, color: 'var(--cyan, #38bdf8)' }}>
                    🛡️ Relay Shields (Pi-hole)
                  </div>
                  <div style={{ 'font-size': '18px', 'font-weight': '700', color: '#4ade80', 'margin-top': '2px' }}>
                    {data().pihole.percentBlocked?.toFixed(1)}% Deflected
                  </div>
                  <div style={{ 'font-size': '11px', opacity: 0.75, 'margin-top': '2px' }}>
                    {data().pihole.blockedDomains?.toLocaleString()} domains blocked
                  </div>
                </div>

                {/* Soul Gem Persona Drift */}
                <div style={{
                  background: 'linear-gradient(135deg, rgba(26,20,42,0.9), rgba(18,14,32,0.85))',
                  border: '1px solid rgba(192,132,252,0.35)', 'border-radius': '8px',
                  padding: '10px 12px', 'box-shadow': '0 4px 14px rgba(0,0,0,0.25)'
                }}>
                  <div style={{ 'font-size': '10px', 'text-transform': 'uppercase', 'letter-spacing': '0.08em', opacity: 0.7, color: '#c084fc' }}>
                    🔮 Soul Gem Attunement
                  </div>
                  <div style={{ 'font-size': '18px', 'font-weight': '700', color: '#c084fc', 'margin-top': '2px' }}>
                    {data().persona ? `${data().persona.driftScore}% Drift` : 'Calibrated'}
                  </div>
                  <div style={{ 'font-size': '11px', opacity: 0.75, 'margin-top': '2px' }}>
                    {data().persona?.driftStatus || 'STABLE & GROUNDED'}
                  </div>
                </div>
              </div>
            </Show>

            {/* Fissal Market Sentinel & Relay Vault Summary */}
            <Show when={data()?.market}>
              <div style={{
                background: 'linear-gradient(135deg, rgba(14,24,38,0.9), rgba(10,18,28,0.85))',
                border: '1px solid rgba(74,222,128,0.3)',
                'border-radius': '8px',
                padding: '10px 12px',
                'margin-bottom': 'var(--sp-2)',
                'box-shadow': '0 4px 14px rgba(0,0,0,0.25)',
                display: 'flex',
                'align-items': 'center',
                'justify-content': 'space-between',
                'flex-wrap': 'wrap',
                gap: '8px',
                cursor: 'pointer'
              }}
              onClick={() => wm.addWidget('fissal-investments')}
              title="Click to open full Fissal Investments panel"
              >
                <div style={{ 'min-width': '0', flex: '1 1 200px' }}>
                  <div style={{ 'font-size': '10px', 'text-transform': 'uppercase', 'letter-spacing': '0.08em', color: '#4ade80', 'font-weight': '700' }}>
                    📈 Fissal Investments & Hardware Vault
                  </div>
                  <div style={{ 'font-size': '15px', 'font-weight': '800', color: '#fff', 'margin-top': '2px' }}>
                    ${Number(data().market.account?.total_value || 496.96).toFixed(2)} USD
                    <span style={{ 'font-size': '11px', 'font-weight': 'normal', color: 'var(--cyan, #38bdf8)', 'margin-left': '8px' }}>
                      (${Number(data().market.account?.cash_buying_power || 420.06).toFixed(2)} Dry Powder)
                    </span>
                  </div>
                  <div style={{ 'font-size': '11px', color: 'var(--t-dim, #94a3b8)', 'margin-top': '2px' }}>
                    SOL · CASHCAT (+14.3% today) · 300+ Penny Radar Matches · Noctua M1 Vault
                  </div>
                </div>
                <div style={{ 'text-align': 'right', 'flex-shrink': '0', 'margin-left': 'auto' }}>
                  <span class="v2-tt-btn" style={{
                    padding: '3px 8px',
                    'font-size': '10px',
                    border: '1px solid rgba(74,222,128,0.35)',
                    background: 'rgba(74,222,128,0.1)',
                    color: '#4ade80',
                    'border-radius': '6px',
                    'font-weight': '600'
                  }}>
                    Open Vault ↗
                  </span>
                </div>
              </div>
            </Show>

            {/* Owner-Gated Dwemer Safe Maintenance Interlock */}
            <Show when={isOwner()}>
              <div style={{
                background: maintStatus()?.isParked
                  ? 'linear-gradient(135deg, rgba(40,25,10,0.95), rgba(25,15,5,0.9))'
                  : 'linear-gradient(135deg, rgba(15,23,42,0.9), rgba(20,29,47,0.85))',
                border: maintStatus()?.isParked
                  ? '1px solid rgba(251,191,36,0.5)'
                  : '1px solid rgba(255,255,255,0.12)',
                'border-radius': '8px',
                padding: '10px 12px',
                'margin-bottom': 'var(--sp-2)',
                'box-shadow': '0 4px 14px rgba(0,0,0,0.3)',
              }}>
                <div style={{ display: 'flex', 'align-items': 'center', 'justify-content': 'space-between', 'margin-bottom': '6px' }}>
                  <span style={{
                    'font-size': '11px', 'text-transform': 'uppercase', 'letter-spacing': '0.08em',
                    'font-weight': '700', color: maintStatus()?.isParked ? 'var(--gold, #ffc857)' : 'var(--cyan, #38bdf8)'
                  }}>
                    ⚙️ Dwemer Maintenance Interlock (Owner Exclusive)
                  </span>
                  <button
                    type="button"
                    class="v2-tt-btn"
                    style={{ padding: '1px 6px', 'font-size': '10px' }}
                    onClick={fetchMaintStatus}
                    disabled={maintBusy()}
                  >↻ Probe</button>
                </div>

                <Show when={!maintStatus()?.isParked}>
                  <div style={{ 'font-size': '12px', color: 'var(--t-dim, #94a3b8)', 'margin-bottom': '8px', 'line-height': '1.4' }}>
                    Server is <strong>active & running</strong>. Dirty disk cache: <code style={{ color: 'var(--gold, #ffc857)' }}>{maintStatus()?.dirtyMb || 0} MB</code>. To relocate power cables or perform chassis work without risking data loss, engage the quiesce latch:
                  </div>
                  <button
                    type="button"
                    class="v2-tt-btn"
                    style={{
                      border: '1px solid rgba(251,191,36,0.4)',
                      background: 'rgba(251,191,36,0.1)',
                      color: 'var(--gold, #ffc857)',
                      'font-weight': '600',
                    }}
                    disabled={maintBusy()}
                    onClick={promptPark}
                  >
                    {maintBusy() ? '⏳ Quiescing Workloads…' : '🛠️ Quiesce Server for Power Relocation'}
                  </button>
                </Show>

                <Show when={maintStatus()?.isParked}>
                  <div style={{
                    padding: '8px 10px', background: 'rgba(251,191,36,0.1)',
                    border: '1px solid rgba(251,191,36,0.3)', 'border-radius': '6px',
                    'margin-bottom': '8px', 'font-size': '12px', color: '#fef08a'
                  }}>
                    ✨ <strong>SERVER IS QUIESCED & DISKS SYNCHRONIZED.</strong> Databases, vector stores, and camera buffers are cleanly halted. 100% safe to relocate power cords or unplug!
                  </div>
                  <div style={{ display: 'flex', gap: '8px', 'flex-wrap': 'wrap' }}>
                    <button
                      type="button"
                      class="v2-tt-btn"
                      style={{
                        background: 'rgba(74,222,128,0.15)',
                        border: '1px solid rgba(74,222,128,0.4)',
                        color: '#4ade80',
                        'font-weight': '600',
                      }}
                      disabled={maintBusy()}
                      onClick={executeResume}
                    >
                      {maintBusy() ? '⏳ Resuming…' : '🟢 Resume Bastion Workloads'}
                    </button>
                    <button
                      type="button"
                      class="btn btn-danger"
                      style={{ 'font-size': '11px', padding: '5px 10px' }}
                      disabled={maintBusy()}
                      onClick={promptPoweroff}
                    >
                      🔴 Execute Immediate Power Off
                    </button>
                  </div>
                </Show>
              </div>
            </Show>

            {/* AI Note */}
            <Show when={data()?.aiNote}>
              <div style={{
                border: '1px solid rgba(245,158,11,.3)', 'border-radius': '8px',
                padding: 'var(--sp-2)', 'margin-bottom': 'var(--sp-2)',
                background: 'linear-gradient(145deg, rgba(245,158,11,.08), rgba(20,15,10,.6))',
                'font-size': '13px', 'line-height': '1.45',
              }}>
                <div style={{ 'font-size': '11px', opacity: 0.8, color: '#f59e0b', 'margin-bottom': '4px', 'font-weight': '600' }}>
                  🐾 Fissal's Watchman Log
                </div>
                {data().aiNote}
              </div>
            </Show>

            {/* Service grid */}
            <div style={{
              display: 'grid', gap: '8px',
              'grid-template-columns': 'repeat(auto-fill, minmax(150px, 1fr))',
            }}>
              <For each={data()?.services || []}>
                {(s) => (
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      'text-decoration': 'none', color: 'inherit',
                      border: `1px solid ${s.up ? 'rgba(74,222,128,.3)' : 'rgba(255,77,45,.4)'}`,
                      'border-radius': '8px', padding: 'var(--sp-2)',
                      display: 'block', background: 'rgba(15,22,33,0.7)',
                      transition: 'all 0.18s ease',
                    }}
                  >
                    <div style={{ 'font-size': '14px' }}>{s.emoji} <strong>{s.name}</strong></div>
                    <div style={{
                      'font-size': '11px', 'margin-top': '4px',
                      color: s.up ? '#4ade80' : '#ff4d2d',
                    }}>
                      {s.up ? `● online · ${s.ms}ms` : '● offline'}
                    </div>
                  </a>
                )}
              </For>
            </div>

            {/* ESO Relay Hub Status */}
            <Show when={data()?.relay?.activeDevices > 0}>
              <div style={{
                'margin-top': 'var(--sp-2)', padding: 'var(--sp-2)',
                border: '1px solid rgba(56,189,248,0.25)', 'border-radius': '8px',
                background: 'rgba(14,26,38,0.6)', display: 'flex', 'align-items': 'center', gap: '10px'
              }}>
                <span style={{ 'font-size': '20px' }}>📡</span>
                <div>
                  <div style={{ 'font-size': '13px', 'font-weight': '600' }}>
                    ESO Relay: {data().relay.activeDevices} Device Connected
                  </div>
                  <div style={{ 'font-size': '11px', opacity: 0.7 }}>
                    {data().relay.devices?.[0]?.device_name || 'Active Client'} (v{data().relay.devices?.[0]?.client_version || '1.3.0'})
                  </div>
                </div>
                <a
                  href="#widget=relay-operations"
                  style={{
                    'margin-left': 'auto', 'font-size': '11px', color: 'var(--cyan, #38bdf8)',
                    'text-decoration': 'none', padding: '3px 8px', border: '1px solid rgba(56,189,248,0.3)',
                    'border-radius': '4px', background: 'rgba(56,189,248,0.1)'
                  }}
                >
                  Manage →
                </a>
              </div>
            </Show>

            {/* Host stats */}
            <Show when={data()?.host}>
              <div style={{
                display: 'flex', gap: '12px', 'margin-top': 'var(--sp-2)',
                'font-size': '12px', opacity: 0.75, 'flex-wrap': 'wrap',
              }}>
                <span>🧮 RAM {data().host.memUsedPct}%</span>
                <span>⚙️ load {data().host.loadAvg} / {data().host.cpus} cores</span>
                <span>⏱️ up {fmtUptime(data().host.uptimeSec)}</span>
              </div>
            </Show>
          </div>
        </Show>
      }>
        <div class="v2-error">{error()}</div>
      </Show>

      {/* Confirmation modal */}
      <Show when={confirm()}>
        <ConfirmDialog
          title={confirm().title}
          body={confirm().body}
          confirmLabel={confirm().confirmLabel}
          confirmKind={confirm().confirmKind}
          onConfirm={() => {
            const action = confirm().action;
            setConfirm(null);
            action();
          }}
          onCancel={() => setConfirm(null)}
        />
      </Show>
    </div>
  );
}
